import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import jsPDF from "jspdf";

const ACTION_OPTIONS = [
  { value: "", label: "Todas as ações" },
  { value: "LOGIN", label: "Entradas no sistema" },
  { value: "LOGOUT", label: "Saídas do sistema" },
  { value: "public_form.submitted", label: "Formulários externos enviados" },
  { value: "file.public_uploaded", label: "Arquivos externos enviados" },
  { value: "file.downloaded", label: "Downloads de arquivos" },
  { value: "API_CALL", label: "Ações em andamento" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};


const parseMetadata = (metadata) => {
  if (!metadata) return null;
  if (typeof metadata !== "string") return metadata;
  try {
    return JSON.parse(metadata);
  } catch {
    return metadata;
  }
};

const ACTION_LABELS = {
  "request.created": "Enviou/criou informação",
  "request.updated": "Alterou informação",
  "request.deleted": "Removeu informação",
  "request.exported": "Exportou dados",
  "request.imported": "Importou dados",
  "request.performed": "Consultou ou baixou",
  "file.downloaded": "Baixou arquivo",
  "file.uploaded": "Enviou arquivo",
  "file.public_uploaded": "Visitante enviou arquivo",
  "public_form.submitted": "Visitante enviou formulário",
  "public_form.upload_blocked": "Upload externo bloqueado",
  LOGIN: "Entrou no sistema",
  LOGOUT: "Saiu do sistema",
};

const getActionLabel = (action) => ACTION_LABELS[action] || String(action || "Ação registrada").replace(/[._]/g, " ");

const getActorLabel = (log) => {
  const metadata = parseMetadata(log.metadata);
  if (metadata?.externalName) return `${metadata.externalName}${metadata.externalEmail ? ` (${metadata.externalEmail})` : ""}`;
  if (log.userName) return log.userName;
  if (log.userId) return `Usuário #${log.userId}`;
  return "Visitante externo";
};

const getResourceLabel = (log) => {
  const metadata = parseMetadata(log.metadata);
  if (metadata?.instanceId) return `Solicitação #${metadata.instanceId}`;
  if (metadata?.processId) return `Processo #${metadata.processId}`;
  if (metadata?.name) return metadata.name;
  const path = log.path || metadata?.path || "";
  if (!path) return "-";
  if (path.includes("/download")) return "Download de arquivo/documento";
  if (path.includes("/public-forms")) return "Formulário público";
  if (path.includes("/upload")) return "Envio de arquivo";
  if (path.includes("/project-bank")) return "Banco de projetos/arquivos";
  return path;
};

const formatDuration = (ms) => {
  if (typeof ms !== "number" || Number.isNaN(ms)) return "-";
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)} h`;
};

const MetadataPreview = ({ metadata }) => {
  const json = parseMetadata(metadata);
  if (!json) return <span className="text-slate-400">-</span>;
  if (typeof json !== "object") return <span className="text-xs text-slate-600">{String(json)}</span>;
  const highlights = [
    json.external ? "Origem: formulário externo" : null,
    json.externalEmail ? `E-mail externo: ${json.externalEmail}` : null,
    json.size ? `Tamanho: ${json.size} bytes` : null,
    Array.isArray(json.submittedFields) ? `Campos enviados: ${json.submittedFields.length}` : null,
  ].filter(Boolean);
  return (
    <details className="text-xs text-slate-600">
      <summary className="cursor-pointer text-blue-600">Ver detalhes</summary>
      {highlights.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] text-slate-600">
          {highlights.map((item) => <li key={item}>{item}</li>)}
        </ul>
      )}
      <pre className="mt-2 max-h-48 overflow-auto rounded bg-slate-900/80 p-2 text-[11px] text-slate-100">
        {JSON.stringify(json, null, 2)}
      </pre>
    </details>
  );
};

export default function AuditLogs({ refreshKey }) {
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    action: "",
    search: "",
    userId: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [responsibles, setResponsibles] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        pageSize,
      };
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          params[key] = value;
        }
      });
      const response = await api.get("/audit/logs", { params });
      setLogs(Array.isArray(response.data?.items) ? response.data.items : []);
      setTotal(Number(response.data?.total) || 0);
    } catch (err) {
      const message = err.response?.data?.error || "Falha ao carregar auditoria";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, pageSize]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, refreshKey]);

  useEffect(() => {
    let mounted = true;
    api.get("/audit/responsibles")
      .then((response) => {
        if (mounted) setResponsibles(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (mounted) setResponsibles([]);
      });
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    const cleared = { startDate: "", endDate: "", action: "", search: "", userId: "" };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  };

  const formatUsage = (ms) => {
    const value = Math.max(0, Number(ms) || 0);
    const totalMinutes = Math.round(value / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours && minutes) return `${hours}h ${minutes}min`;
    if (hours) return `${hours}h`;
    return `${minutes}min`;
  };

  const generateAuditPdf = async () => {
    setReportLoading(true);
    setError(null);
    try {
      const params = {};
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          params[key] = value;
        }
      });
      const response = await api.get("/audit/report", { params });
      const report = response.data || {};
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - margin * 2;
      const drawHeader = (subtitle = "") => {
        doc.setFillColor(7, 31, 84);
        doc.rect(0, 0, pageWidth, 30, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(17);
        doc.text("Relatório de Auditoria do Sistema", margin, 13);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(subtitle || `Emitido em ${new Date().toLocaleString("pt-BR")}`, margin, 21);
        doc.setTextColor(15, 23, 42);
      };
      drawHeader();

      const selectedResponsible = responsibles.find(
        (item) => String(item.userId) === String(appliedFilters.userId),
      );
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Filtros aplicados", margin, 40);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const filterText = [
        appliedFilters.startDate ? `De: ${appliedFilters.startDate}` : null,
        appliedFilters.endDate ? `Até: ${appliedFilters.endDate}` : null,
        selectedResponsible ? `Responsável: ${selectedResponsible.userName}` : "Responsável: todos",
        appliedFilters.action ? `Ação: ${getActionLabel(appliedFilters.action)}` : "Ação: todas",
        appliedFilters.search ? `Busca: ${appliedFilters.search}` : null,
      ].filter(Boolean).join(" • ");
      doc.text(doc.splitTextToSize(filterText, contentWidth), margin, 46);

      const summaryY = 58;
      const cards = [
        ["Registros", String(report.total || 0)],
        ["Falhas HTTP", String(report.failedActions || 0)],
        ["Duração média requisição", formatDuration(Number(report.averageRequestDurationMs || 0))],
        ["Responsáveis com atividade", String((report.usageByUser || []).length)],
      ];
      const cardWidth = (contentWidth - 9) / 4;
      cards.forEach(([label, value], index) => {
        const x = margin + index * (cardWidth + 3);
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(x, summaryY, cardWidth, 20, 2, 2, "FD");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(label, x + 3, summaryY + 6);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(15, 23, 42);
        doc.text(String(value), x + 3, summaryY + 14);
      });

      let y = 88;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Tempo médio de uso por dia", margin, y);
      y += 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text(
        "Estimativa calculada somente durante a geração do relatório, a partir das atividades já auditadas. Intervalos sem atividade acima de 30 minutos não são contabilizados.",
        margin,
        y,
      );
      y += 7;
      const usage = Array.isArray(report.usageByUser) ? report.usageByUser : [];
      const usageCols = [70, 38, 45, 45, 35];
      const usageHeaders = ["Responsável", "Dias ativos", "Tempo total", "Média por dia", "Eventos"];
      const drawUsageHeader = () => {
        let x = margin;
        doc.setFillColor(239, 246, 255);
        doc.rect(margin, y, usageCols.reduce((sum, value) => sum + value, 0), 8, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(30, 64, 175);
        usageHeaders.forEach((label, index) => {
          doc.text(label, x + 2, y + 5.5);
          x += usageCols[index];
        });
        y += 8;
      };
      drawUsageHeader();
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 65, 85);
      usage.slice(0, 25).forEach((entry) => {
        if (y > pageHeight - 18) {
          doc.addPage();
          drawHeader("Continuação • tempo de uso");
          y = 38;
          drawUsageHeader();
        }
        let x = margin;
        const values = [
          entry.userName || `Usuário #${entry.userId}`,
          String(entry.activeDays || 0),
          formatUsage(entry.totalActiveMs),
          formatUsage(entry.averageDailyMs),
          String(entry.eventCount || 0),
        ];
        values.forEach((value, index) => {
          doc.text(doc.splitTextToSize(String(value), usageCols[index] - 4), x + 2, y + 5.5);
          x += usageCols[index];
        });
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, y + 8, margin + usageCols.reduce((sum, value) => sum + value, 0), y + 8);
        y += 8;
      });

      doc.addPage();
      drawHeader("Registros detalhados");
      y = 38;
      const cols = [32, 48, 42, 105, 18, 25];
      const headers = ["Data/Hora", "Responsável", "Ação", "Descrição", "Status", "Duração"];
      const drawLogHeader = () => {
        let x = margin;
        doc.setFillColor(239, 246, 255);
        doc.rect(margin, y, contentWidth, 8, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(30, 64, 175);
        headers.forEach((label, index) => {
          doc.text(label, x + 1.5, y + 5.5);
          x += cols[index];
        });
        y += 8;
      };
      drawLogHeader();
      const items = Array.isArray(report.items) ? report.items : [];
      items.forEach((item) => {
        const values = [
          formatDateTime(item.createdAt),
          getActorLabel(item),
          getActionLabel(item.action),
          item.description || getResourceLabel(item) || "-",
          item.statusCode == null ? "-" : String(item.statusCode),
          formatDuration(Number(item.durationMs || 0)),
        ];
        const lines = values.map((value, index) => doc.splitTextToSize(String(value), cols[index] - 3));
        const rowHeight = Math.max(7, ...lines.map((line) => line.length * 3.4 + 2));
        if (y + rowHeight > pageHeight - 12) {
          doc.addPage();
          drawHeader("Registros detalhados • continuação");
          y = 38;
          drawLogHeader();
        }
        let x = margin;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(51, 65, 85);
        lines.forEach((line, index) => {
          doc.text(line, x + 1.5, y + 4.8);
          x += cols[index];
        });
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, y + rowHeight, margin + contentWidth, y + rowHeight);
        y += rowHeight;
      });
      if (report.truncated) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        doc.text(
          `O PDF detalha os ${report.returned} registros mais recentes entre ${report.total} encontrados. Os indicadores consideram a janela de auditoria consultada.`,
          margin,
          pageHeight - 6,
        );
      }

      doc.save(`auditoria-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      setError(err.response?.data?.error || "Falha ao gerar relatório de auditoria");
    } finally {
      setReportLoading(false);
    }
  };

  const canGoBack = page > 1;
  const canGoForward = page < totalPages;

  return (
    <div className="space-y-4 p-3">
      <p className="text-sm text-slate-600">
        Acompanhe entradas, saídas e ações realizadas por todos os usuários. Somente administradores podem visualizar estes registros.
      </p>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800">Filtros</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          <label className="flex flex-col text-sm text-slate-700">
            Data inicial
            <input
              type="date"
              value={filters.startDate}
              onChange={(event) => setFilters((prev) => ({ ...prev, startDate: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
            />
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Data final
            <input
              type="date"
              value={filters.endDate}
              onChange={(event) => setFilters((prev) => ({ ...prev, endDate: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
            />
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Responsável
            <select
              value={filters.userId}
              onChange={(event) => setFilters((prev) => ({ ...prev, userId: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
            >
              <option value="">Todos os responsáveis</option>
              {responsibles.map((item) => (
                <option key={item.userId} value={item.userId}>
                  {item.userName}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Tipo de ação
            <select
              value={filters.action}
              onChange={(event) => setFilters((prev) => ({ ...prev, action: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
            >
              {ACTION_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-sm text-slate-700 md:col-span-2 lg:col-span-3">
            Buscar por descrição, recurso, usuário interno ou visitante externo
            <input
              type="text"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
              placeholder="Ex.: João, formulário público, arquivo, solicitação"
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={applyFilters}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Aplicar filtros
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={generateAuditPdf}
            disabled={reportLoading}
            className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-wait disabled:opacity-60"
          >
            {reportLoading ? "Gerando PDF..." : "Gerar relatório PDF"}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Registros encontrados</h2>
            <p className="text-sm text-slate-600">
              {loading
                ? "Carregando registros..."
                : total === 0
                ? "Nenhum registro encontrado para os filtros aplicados."
                : `${total} registro${total === 1 ? "" : "s"} localizado${total === 1 ? "" : "s"}.`}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <span>Itens por página</span>
            <select
              value={pageSize}
              onChange={(event) => {
                const newSize = Number(event.target.value);
                setPageSize(newSize);
                setPage(1);
              }}
              className="rounded border border-slate-300 px-2 py-1"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">{error}</p>}

        {!error && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Data/Hora</th>
                  <th className="px-3 py-2 text-left">Usuário</th>
                  <th className="px-3 py-2 text-left">Ação</th>
                  <th className="px-3 py-2 text-left">Descrição</th>
                  <th className="px-3 py-2 text-left">Recurso</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Duração</th>
                  <th className="px-3 py-2 text-left">Origem</th>
                  <th className="px-3 py-2 text-left">Detalhes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                      Carregando auditoria...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                      Nenhum registro para exibir.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700">{formatDateTime(log.createdAt)}</td>
                      <td className="px-3 py-2 text-slate-700">
                        <span className="font-medium text-slate-800">{getActorLabel(log)}</span>
                        {log.userId && (
                          <span className="ml-2 text-xs text-slate-500">ID {log.userId}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                          {getActionLabel(log.action)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{log.description || "-"}</td>
                      <td className="px-3 py-2 text-slate-600">
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-slate-700">{getResourceLabel(log)}</span>
                          <span className="truncate text-[11px] text-slate-500" title={log.path || undefined}>
                            {log.path || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{log.statusCode ?? "-"}</td>
                      <td className="px-3 py-2 text-slate-600">{formatDuration(log.durationMs)}</td>
                      <td className="px-3 py-2 text-slate-600">
                        <div className="flex flex-col">
                          <span className="text-xs text-slate-600" title={log.ip || undefined}>
                            {log.ip || "-"}
                          </span>
                          <span className="truncate text-[11px] text-slate-500" title={log.userAgent || undefined}>
                            {log.userAgent || ""}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <MetadataPreview metadata={log.metadata} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 pt-3 text-sm text-slate-700 md:flex-row">
          <div>
            Página {page} de {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => canGoBack && setPage((prev) => Math.max(1, prev - 1))}
              disabled={!canGoBack}
              className="rounded border border-slate-300 px-3 py-1 text-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => canGoForward && setPage((prev) => prev + 1)}
              disabled={!canGoForward}
              className="rounded border border-slate-300 px-3 py-1 text-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
